import * as React from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import SingleDatePickerMAIQ from 'app/components/SingleDatePickerMAIQ';
import FileUploadMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/FileUploadMAIQ';
import { SortableChipList } from 'app/pages/NurturingJourneySettingsComponent/components/SortableChipList';
function formatTypeLabel(value: string): string {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

interface TemplateStepProps {
  templateState: any;
  setTemplateState: React.Dispatch<React.SetStateAction<any>>;
  error: Record<string, boolean>;
  html?: boolean;
  templateTypes: string[];
  templateTypesLoading?: boolean;
  staticApplicableTypeOptions?: string[];
  onTypeChange?: (type: string) => void;
  onApplicableTypesChange?: (types: string[]) => void;
  htmlFile?: File | null;
  onHtmlFileSelect?: (file: File) => void;
}

export default function TemplateStep({
  templateState,
  setTemplateState,
  error,
  html,
  templateTypes,
  templateTypesLoading = false,
  staticApplicableTypeOptions = [],
  onTypeChange,
  onApplicableTypesChange,
  htmlFile = null,
  onHtmlFileSelect,
}: TemplateStepProps) {
  const trackingId = templateState.tracking_id ?? '';
  const generatedType = templateState.generated_type ?? '';
  const applicableTypes = Array.isArray(templateState.applicable_types)
    ? templateState.applicable_types
    : [];
  const isStaticGenerated = generatedType === 'static';

  return (
    <Stack spacing={3}>
      <Typography
        variant="h5"
        gutterBottom
        component="div"
        sx={{ marginBottom: '10px', fontSize: '16px' }}
      >
        Create Template
      </Typography>

      <TextField
        id="template_name"
        label="Name"
        fullWidth
        value={templateState.name}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setTemplateState({
            ...templateState,
            name: event.target.value,
          });
        }}
        error={error.name ? true : false}
      />
      <TextField
        id="template_subject"
        label="Subject"
        fullWidth
        value={templateState.subject}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setTemplateState({
            ...templateState,
            subject: event.target.value,
          });
        }}
        error={error.subject ? true : false}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'stretch' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FormControl fullWidth error={error.type ? true : false}>
            <InputLabel id="template_type_label">Type</InputLabel>
            <Select
              labelId="template_type_label"
              id="template_type"
              label="Type"
              value={templateState.type}
              onChange={event => {
                if (onTypeChange) {
                  onTypeChange(String(event.target.value));
                  return;
                }
                setTemplateState({
                  ...templateState,
                  type: event.target.value,
                });
              }}
              disabled={templateTypesLoading}
            >
              <MenuItem value="" disabled>
                {templateTypesLoading ? 'Loading types...' : 'Select type'}
              </MenuItem>
              {templateTypes.map(type => (
                <MenuItem key={type} value={type}>
                  {formatTypeLabel(type)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {generatedType && (
          <Box sx={{ display: 'flex', alignItems: 'stretch', width: 'fit-content' }}>
            <Box
              sx={{
                height: 56,
                border: '1px solid',
                borderColor: error.generated_type ? 'error.main' : 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'background.paper',
                width: 'fit-content',
              }}
            >
              <Chip
                label={`${formatTypeLabel(generatedType)} Template`}
                size="small"
                sx={{
                  bgcolor: generatedType === 'static' ? 'secondary.main' : 'primary.main',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              />
            </Box>
            {error.generated_type && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                Generated type is required
              </FormHelperText>
            )}
          </Box>
        )}
      </Box>

      {isStaticGenerated && (
        <FormControl fullWidth error={error.applicable_types ? true : false}>
          <InputLabel id="template_applicable_types_label">
            Applicable Types
          </InputLabel>
          <Select
            labelId="template_applicable_types_label"
            id="template_applicable_types"
            multiple
            value={applicableTypes}
            onChange={event => {
              const nextValues = Array.isArray(event.target.value)
                ? event.target.value
                : [String(event.target.value)];
              if (onApplicableTypesChange) {
                onApplicableTypesChange(nextValues as string[]);
                return;
              }
              setTemplateState({
                ...templateState,
                applicable_types: nextValues,
              });
            }}
            input={<OutlinedInput label="Applicable Types" />}
            renderValue={selected => {
              const arr = selected as string[];
              return (
                <Box component="span" sx={{ display: 'block', minHeight: 32 }}>
                  <SortableChipList
                    items={arr}
                    onReorder={newOrder => {
                      if (onApplicableTypesChange) {
                        onApplicableTypesChange(newOrder);
                        return;
                      }
                      setTemplateState({
                        ...templateState,
                        applicable_types: newOrder,
                      });
                    }}
                  />
                </Box>
              );
            }}
          >
            {staticApplicableTypeOptions.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
          {error.applicable_types && (
            <FormHelperText error>
              Please select at least one applicable type
            </FormHelperText>
          )}
        </FormControl>
      )}

      <SingleDatePickerMAIQ
        label="Expiration Date"
        value={templateState.expiration_date ?? null}
        onChange={dateStr => {
          setTemplateState({
            ...templateState,
            expiration_date: dateStr ?? '',
          });
        }}
        error={error.expiration_date ? true : false}
      />
      {html && (
        <FormControl fullWidth error={error.html ?? false}>
          <Typography
            component="label"
            variant="subtitle2"
            sx={{
              display: 'block',
              mb: 1,
              fontWeight: 500,
              color: error.html ? 'error.main' : 'text.primary',
            }}
          >
            Html
          </Typography>
          <FileUploadMAIQ
            id="template_html_upload"
            accept=".html,.htm,text/html"
            selectedFile={htmlFile}
            onFileSelect={onHtmlFileSelect ?? (() => {})}
            supportedFormatsText="Supported: HTML files (.html, .htm)"
            minHeight={260}
          />
          {error.html && (
            <FormHelperText error>HTML file is required</FormHelperText>
          )}
        </FormControl>
      )}

      {/* Tracking ID: same subtle style as Edit/Preview - below Html, small italic */}
      {trackingId ? (
        <Typography
          variant="caption"
          sx={{
            fontStyle: 'italic',
            color: 'text.secondary',
          }}
        >
          Tracking ID: {trackingId}
        </Typography>
      ) : null}
    </Stack>
  );
}
